import React, { useCallback, useMemo, useRef } from "react";
import type { Props as FieldProps } from "../Form/Field";
import type { DateRangePickerStateOptions } from "react-stately";
import { useDateRangePickerState } from "react-stately";
import type { DateValue, PositionProps, TimeValue } from "react-aria";
import { useDateRangePicker } from "react-aria";
import ButtonIconV2 from "../ButtonIconV2";
import { CalendarLine } from "../IconV2/react";
import { Dialog, Popover } from "react-aria-components";
import { DialogWrapper } from "../DatePicker/DatePicker";
import RangeCalendar from "./RangeCalendar";
import DateRangeField from "./DateRangeField";
import { CalendarDateTime, Time } from "@internationalized/date";
import type { Preset } from "./PresetSelector";
import PresetSelector from "./PresetSelector";
import { styled } from "@linaria/react";
import { colors } from "../Theme";
import { TimeField } from "../TimeField/TimeField";
import t from "../i18n";
import type { MappedDateValue } from "@react-types/datepicker";
import type { AriaLabelingProps, RangeValue } from "@react-types/shared";
import { css, cx } from "@linaria/core";
import Button from "../Button";
import { color } from "..";
import TooltipV2 from "../TooltipV2";

export interface DateRangePickerProps<T extends DateValue, PresetId extends string>
  extends Omit<FieldProps, "afterInputSlot"> {
  dateOptions?: Omit<DateRangePickerStateOptions<T>, "hourCycle" | "onChange"> &
    Pick<AriaLabelingProps, "aria-label" | "aria-labelledby"> & {
      onChange?: (value: RangeValue<MappedDateValue<T>> | null) => void;
    };
  defaultTimeValues?: {
    start: TimeValue;
    end: TimeValue;
  };
  presets?: Preset<T, PresetId>[];
  popoverPlacement?: PositionProps["placement"];
  manualValidation?: boolean;
  rangeToResetTo?: RangeValue<MappedDateValue<T>>;
  hideCalendar?: boolean;
}

function DateRangePicker<T extends DateValue, PresetId extends string>({
  dateOptions = {},
  defaultTimeValues,
  presets,
  popoverPlacement = "bottom start",
  manualValidation = false,
  rangeToResetTo,
  hideCalendar = false,
  ...fieldProps
}: DateRangePickerProps<T, PresetId>) {
  const getTimeValueFromDateValue = React.useCallback(
    (value: MappedDateValue<T> | null | undefined) => {
      if (value === null || value === undefined) return null;

      const timeValue = value as MappedDateValue<T> & {
        hour?: number;
        millisecond?: number;
        minute?: number;
        second?: number;
      };

      return new Time(
        timeValue.hour ?? 0,
        timeValue.minute ?? 0,
        timeValue.second ?? 0,
        timeValue.millisecond ?? 0,
      );
    },
    [],
  );

  const updateTempRangeTime = React.useCallback((key: "start" | "end", value: TimeValue | null) => {
    if (value === null) return;

    setTempRangeValue(prev => {
      if (prev === null) return prev;

      const currentValue = prev[key];
      if (currentValue === null || currentValue === undefined) {
        return prev;
      }

      return {
        ...prev,
        [key]: currentValue.set({
          hour: value.hour,
          minute: value.minute,
          second: value.second,
          millisecond: value.millisecond,
        }) as MappedDateValue<T>,
      };
    });
  }, []);

  const defaultOptions: DateRangePickerStateOptions<T> = React.useMemo(
    () => ({
      shouldCloseOnSelect: false,
      hourCycle: 24,
    }),
    [],
  );

  const mergedOptions = React.useMemo<DateRangePickerStateOptions<T>>(() => {
    const { onChange: dateOptionsOnChange, ...restDateOptions } = dateOptions;

    return {
      ...defaultOptions,
      ...restDateOptions,
      onChange(value: RangeValue<MappedDateValue<T>> | null) {
        if (manualValidation) {
          setTempRangeValue(value ?? null);
        } else {
          dateOptionsOnChange?.(value);
        }
      },
    };
  }, [defaultOptions, dateOptions, manualValidation]);

  const state = useDateRangePickerState(mergedOptions);

  const [tempRangeValue, setTempRangeValue] = React.useState<RangeValue<MappedDateValue<T>> | null>(
    () => (manualValidation ? (state.value as RangeValue<MappedDateValue<T>>) : null),
  );

  const preset = useMemo(
    () =>
      presets?.find(p => {
        const presetStart = typeof p.startDate === "function" ? p.startDate() : p.startDate;
        const presetEnd = typeof p.endDate === "function" ? p.endDate() : p.endDate;
        const sameStart =
          state.value.start
            ?.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
            .compare(presetStart.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })) === 0;
        /** @note be loose on end preset matching */
        const sameEnd =
          state.value.end
            ?.set({ millisecond: 999, hour: 23, minute: 59, second: 59 })
            .compare(presetEnd.set({ millisecond: 999, hour: 23, minute: 59, second: 59 })) === 0;

        const inRange = state.value.start && state.value?.end && sameStart && sameEnd;
        return inRange;
      }) ?? null,
    [presets, state.value],
  );

  const [hasEditedWithKeyboard, setHasEditedWithKeyboard] = React.useState(false);

  const calendarSelectionTimeRange = React.useMemo(
    () => ({
      start: defaultTimeValues?.start ?? new Time(0, 0, 0, 0),
      end: defaultTimeValues?.end ?? new Time(23, 59, 59, 999),
    }),
    [defaultTimeValues],
  );

  const clearBlur = useCallback(() => {
    window.setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 1);
  }, []);

  const applyChanges = React.useCallback(() => {
    if (tempRangeValue) {
      dateOptions.onChange?.(tempRangeValue);
    }
    clearBlur();
    state.setOpen(false);
  }, [clearBlur, tempRangeValue, dateOptions, state]);

  const resetRange = React.useCallback(() => {
    if (manualValidation && rangeToResetTo) {
      setTempRangeValue(rangeToResetTo);
      state.setValue(rangeToResetTo);
      dateOptions.onChange?.(rangeToResetTo);
      return;
    }

    const minMaxRange = {
      start: dateOptions.minValue,
      end: dateOptions.maxValue,
    } as RangeValue<MappedDateValue<T>>;

    if (manualValidation && dateOptions.minValue && dateOptions.maxValue) {
      setTempRangeValue(minMaxRange);
      state.setValue(minMaxRange);
    } else {
      setTempRangeValue(minMaxRange);
    }
    dateOptions.onChange?.(minMaxRange);
  }, [state, manualValidation, rangeToResetTo, dateOptions]);

  const cancelLastChanges = React.useCallback(() => {
    setTempRangeValue({
      start: state.value?.start,
      end: state.value?.end,
    } as RangeValue<MappedDateValue<T>>);
    // setPreset(findPresetInRange());
  }, [state.value, setTempRangeValue]);

  const triggerRef = React.useRef<HTMLDivElement>(null);

  const { startFieldProps, endFieldProps, buttonProps, dialogProps, calendarProps } =
    useDateRangePicker(
      {
        ...dateOptions,
        label: fieldProps.label,
      },
      state,
      triggerRef,
    );

  delete buttonProps.onPress;

  const hasTime = mergedOptions.granularity !== undefined && state.hasTime;
  const timeGranularity = React.useMemo(() => {
    switch (mergedOptions.granularity) {
      case "hour":
        return "hour";
      case "minute":
        return "minute";
      case "second":
        return "second";
      default:
        return "minute";
    }
  }, [mergedOptions.granularity]);

  React.useEffect(() => {
    if (hasTime && state.timeRange === null) {
      state.setTimeRange({
        start: defaultTimeValues?.start ?? new Time(0, 0, 0, 0),
        end: defaultTimeValues?.end ?? new Time(23, 59, 59, 999),
      });
    }
  }, [defaultTimeValues, hasTime, mergedOptions.granularity, state]);

  const calendarButtonRef = useRef<HTMLButtonElement>(null);

  const closeOnClickOutside = React.useCallback(
    (e: MouseEvent) => {
      if (!(e.target as HTMLElement)?.closest(".PickerContainer") && state.isOpen) {
        if (manualValidation) cancelLastChanges();

        state.setOpen(false);
        window.setTimeout(() => (document.activeElement as HTMLElement | null)?.blur());
      }
    },
    [manualValidation, cancelLastChanges, state],
  );

  React.useEffect(() => {
    window.addEventListener("click", closeOnClickOutside, { passive: true });
    return () => window.removeEventListener("click", closeOnClickOutside);
  }, [closeOnClickOutside]);

  const fieldWrapperRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const valueFromProps = dateOptions.value as RangeValue<MappedDateValue<T>> | null;
    if (!valueFromProps || !tempRangeValue) return;

    const isSame =
      tempRangeValue?.start?.compare(valueFromProps.start) === 0 &&
      tempRangeValue.end.compare(valueFromProps.end) === 0;

    if (!state.isOpen && !isSame && !hasEditedWithKeyboard) {
      setTempRangeValue(valueFromProps);
      state.setValue(valueFromProps);
    }
    // If the focus is on an editable segment, do nothing (no blur, no sync)
  }, [dateOptions.value, hasEditedWithKeyboard, state, tempRangeValue]);

  return (
    <div ref={triggerRef}>
      <DateRangeField
        state={state}
        hideCalendar={hideCalendar}
        preset={preset}
        // clearPreset={() => setPreset(null)}
        {...fieldProps}
        startDateOptions={{
          ...startFieldProps,
        }}
        endDateOptions={{
          ...endFieldProps,
        }}
        afterInputSlot={
          <TooltipV2 overlay={t("daterangepicker.tooltip.open_calendar")} placement="top">
            <div>
              <StyledButtonIconV2
                ref={calendarButtonRef}
                {...buttonProps}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  state.setOpen(!state.isOpen);
                }}
              >
                <CalendarLine />
              </StyledButtonIconV2>
            </div>
          </TooltipV2>
        }
        onTemporaryChange={newRange => {
          setTempRangeValue(newRange as RangeValue<MappedDateValue<T>>);
        }}
        resetRange={resetRange}
        applyChanges={applyChanges}
        cancelChanges={cancelLastChanges}
        manualValidation={manualValidation}
        tempRangeValue={tempRangeValue}
        isDateUnavailable={dateOptions.isDateUnavailable}
        wrapperRef={fieldWrapperRef}
        hasEditedWithKeyboard={hasEditedWithKeyboard}
        setHasEditedWithKeyboard={setHasEditedWithKeyboard}
      />
      <Popover
        triggerRef={triggerRef}
        isOpen={state.isOpen}
        placement={hideCalendar ? "bottom end" : popoverPlacement}
        isNonModal
        shouldUpdatePosition
        offset={4}
        shouldCloseOnInteractOutside={() => !manualValidation}
        className={cx("PickerContainer", popover)}
      >
        <Dialog {...dialogProps}>
          <DialogWrapper>
            <div className={mainWrapper}>
              <div className={cx(calendar, manualValidation ? "" : rowDirection)}>
                {!hideCalendar && (
                  <RangeCalendar
                    {...calendarProps}
                    value={manualValidation ? tempRangeValue : undefined}
                    hasTime={false}
                    preset={preset}
                    onChange={value => {
                      if (!value.start || !value.end) return;
                      const val = {
                        start: new CalendarDateTime(
                          value.start.year,
                          value.start.month,
                          value.start.day,
                          calendarSelectionTimeRange.start.hour,
                          calendarSelectionTimeRange.start.minute,
                          calendarSelectionTimeRange.start.second,
                          calendarSelectionTimeRange.start.millisecond,
                        ) as MappedDateValue<T>,
                        end: new CalendarDateTime(
                          value.end.year,
                          value.end.month,
                          value.end.day,
                          calendarSelectionTimeRange.end.hour,
                          calendarSelectionTimeRange.end.minute,
                          calendarSelectionTimeRange.end.second,
                          calendarSelectionTimeRange.end.millisecond,
                        ) as MappedDateValue<T>,
                      };
                      if (manualValidation) {
                        setTempRangeValue(val);
                      } else {
                        state.setValue(val);
                        dateOptions.onChange?.(val);
                        const shouldCloseOnSelect =
                          typeof mergedOptions.shouldCloseOnSelect === "function"
                            ? mergedOptions.shouldCloseOnSelect()
                            : mergedOptions.shouldCloseOnSelect;
                        if (shouldCloseOnSelect) {
                          state.setOpen(false);
                        }
                      }
                      clearBlur();
                    }}
                  />
                )}
                {hasTime && (
                  <TimeWrapper>
                    <StyledTimeField
                      label={t("daterangepicker.start_time")}
                      id={`${fieldProps.id}-start-time`}
                      timeOptions={{
                        value: manualValidation
                          ? getTimeValueFromDateValue(tempRangeValue?.start)
                          : state.timeRange?.start || null,
                        onChange: v => {
                          if (manualValidation) {
                            updateTempRangeTime("start", v);
                            return;
                          }

                          if (v !== null) {
                            return state.setTime("start", v);
                          }
                        },
                        granularity: timeGranularity,
                        hideTimeZone: dateOptions?.hideTimeZone,
                      }}
                    />

                    <StyledTimeField
                      label={t("daterangepicker.end_time")}
                      id={`${fieldProps.id}-end-time`}
                      timeOptions={{
                        value: manualValidation
                          ? getTimeValueFromDateValue(tempRangeValue?.end)
                          : state.timeRange?.end || null,
                        onChange: v => {
                          if (manualValidation) {
                            updateTempRangeTime("end", v);
                            return;
                          }

                          if (v !== null) {
                            return state.setTime("end", v);
                          }
                        },
                        granularity: timeGranularity,
                        hideTimeZone: dateOptions?.hideTimeZone,
                      }}
                    />
                  </TimeWrapper>
                )}
                {presets !== undefined && presets.length > 0 && (
                  <PresetSelector
                    presets={presets}
                    state={state}
                    onSelectionChange={(_, start, end) => {
                      if (manualValidation) {
                        if (start && end) {
                          setTempRangeValue({
                            start,
                            end,
                          } as unknown as RangeValue<MappedDateValue<T>>);
                        }
                      }

                      clearBlur();
                    }}
                    manualValidation={manualValidation}
                    tempRangeValue={tempRangeValue}
                  />
                )}
              </div>
              {manualValidation && (
                <div className={footer}>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      cancelLastChanges();
                      state.setOpen(false);
                    }}
                  >
                    {t("daterangepicker.cancel")}
                  </Button>
                  <Button variant="primary" onClick={applyChanges}>
                    {t("daterangepicker.apply")}
                  </Button>
                </div>
              )}
            </div>
          </DialogWrapper>
        </Dialog>
      </Popover>
    </div>
  );
}

export default DateRangePicker;

//#region
const popover = css`
  pointer-events: auto;
`;

const StyledButtonIconV2 = styled(ButtonIconV2)`
  &:focus {
    outline: 2px solid ${colors.lightNitrogen100};
  }
`;

const TimeWrapper = styled.div`
  padding: 12px;
  display: flex;
  gap: 16px;
`;

const StyledTimeField = styled(TimeField)`
  width: 120px;
`;

const mainWrapper = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const calendar = css`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const rowDirection = css`
  flex-direction: row;
`;

const footer = css`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid ${color.titanium100};
`;

//#endregion
